import type { RootStore } from 'logic';
import { RouteNames } from 'logic/routes';
import { STEP_TAB_DONE, STEP_TAB_TRIAGE } from 'logic/ui-active-session';
import { normalizeSearchText } from 'utils/textSearch';
import {
  action,
  computed,
  makeObservable,
  observable,
  ObservableSet,
  reaction,
  runInAction,
} from 'mobx';
import { getStaticStepDisplay } from './stepDisplayUtils';
import { getAssetKey } from './asset-collections';
import { matchesCollectionMemberFilter } from './collection-status-sections';
import type { CollectionMemberStatus } from '@creative-kit/shared';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface AssetItem {
  rowId: string;
  sourceId: string;
}

export type AssetWorkflowStatus =
  | { kind: 'final'; label: string }
  | { kind: 'step'; label: string; stepId: string }
  | { kind: 'not_started'; label: string };

export interface AssetBucketStep {
  stepId: string;
  label: string;
  count: number;
}

export interface AssetBucketDefinition {
  key: string;
  label: string;
  count: number;
  filterValue: string | null;
  kind: 'all' | 'final' | 'step' | 'pending' | 'not_started';
}

export interface SearchAssetBucketItem extends AssetBucketDefinition {
  totalCount?: number;
  previewAssets: AssetItem[];
}

export interface ConnectedCardFocusNavigation {
  routeName: RouteNames.CARD;
  params: {
    sessionId: string;
    cardId: string;
  };
  stepId: string;
}

interface BuildAssetBucketsArgs {
  totalCount: number;
  doneCount: number;
  visibleSteps: AssetBucketStep[];
  pendingCount: number;
  noCardsConnectedCount: number;
  activeFilter?: string | null;
}

interface ResolveEffectiveAssetSourceFilterArgs {
  activeSourceFilter: string[];
  defaultSourceIds?: string[];
  defaultsApplied: boolean;
}

export const PENDING_ASSET_LABEL = getStaticStepDisplay(STEP_TAB_TRIAGE)?.label ?? 'Pending';
export const NO_CARDS_CONNECTED_LABEL = 'No Cards Connected';
export const ASSET_ROUTE_ALL = 'all';
export const ASSET_ROUTE_PENDING = 'pending';
export const LEGACY_ASSET_ROUTE_PENDING = 'triage';
export const ASSET_ROUTE_DONE = 'done';
export const LEGACY_ASSET_ROUTE_DONE = 'final';
export const ASSET_ROUTE_NO_CARD_CONNECTED = 'no-card-connected';
export const LEGACY_ASSET_ROUTE_NO_CARD_CONNECTED = 'not_started';

const LAST_ASSETS_BUCKET_STORAGE_KEY = (sessionId: string) => `cq:lastAssetsBucket:${sessionId}`;

export function getAssetRouteBucketId(filterValue: string | null): string {
  if (filterValue === null) {
    return ASSET_ROUTE_ALL;
  }
  if (filterValue === STEP_TAB_TRIAGE) {
    return ASSET_ROUTE_PENDING;
  }
  if (filterValue === 'final') {
    return ASSET_ROUTE_DONE;
  }
  if (filterValue === 'not_started') {
    return ASSET_ROUTE_NO_CARD_CONNECTED;
  }
  return filterValue;
}

export function assetRouteBucketToFilterValue(routeBucketId: string): string | null {
  if (routeBucketId === ASSET_ROUTE_ALL) {
    return null;
  }
  if (routeBucketId === ASSET_ROUTE_PENDING || routeBucketId === LEGACY_ASSET_ROUTE_PENDING) {
    return STEP_TAB_TRIAGE;
  }
  if (routeBucketId === ASSET_ROUTE_DONE || routeBucketId === LEGACY_ASSET_ROUTE_DONE) {
    return 'final';
  }
  if (
    routeBucketId === ASSET_ROUTE_NO_CARD_CONNECTED ||
    routeBucketId === LEGACY_ASSET_ROUTE_NO_CARD_CONNECTED
  ) {
    return 'not_started';
  }
  return routeBucketId;
}

export function isBuiltInAssetRouteBucketId(routeBucketId: string): boolean {
  return (
    routeBucketId === ASSET_ROUTE_ALL ||
    routeBucketId === ASSET_ROUTE_PENDING ||
    routeBucketId === ASSET_ROUTE_DONE ||
    routeBucketId === ASSET_ROUTE_NO_CARD_CONNECTED
  );
}

export function isValidAssetRouteBucketId(
  routeBucketId: string,
  sessionStepIds: string[] = [],
): boolean {
  const normalizedRouteBucketId = normalizeAssetRouteBucketId(routeBucketId);
  return (
    isBuiltInAssetRouteBucketId(normalizedRouteBucketId) ||
    sessionStepIds.includes(normalizedRouteBucketId)
  );
}

export function getCanonicalAssetsRouteBucketId({
  sessionId,
  activeStatusFilter,
  sessionStepIds = [],
  validateCustomStepIds = false,
}: {
  sessionId: string;
  activeStatusFilter: string | null;
  sessionStepIds?: string[];
  validateCustomStepIds?: boolean;
}): string {
  const validateBucketId = (routeBucketId: string): string => {
    if (validateCustomStepIds && !isValidAssetRouteBucketId(routeBucketId, sessionStepIds)) {
      return ASSET_ROUTE_ALL;
    }
    return routeBucketId;
  };

  if (activeStatusFilter !== null) {
    return validateBucketId(getAssetRouteBucketId(activeStatusFilter));
  }

  try {
    const storedBucketId = localStorage.getItem(LAST_ASSETS_BUCKET_STORAGE_KEY(sessionId));
    return storedBucketId
      ? validateBucketId(normalizeAssetRouteBucketId(storedBucketId))
      : ASSET_ROUTE_ALL;
  } catch {
    return ASSET_ROUTE_ALL;
  }
}

export function normalizeAssetRouteBucketId(
  routeBucketId: string,
  sessionStepIds?: string[],
  validateCustomStepIds = false,
): string {
  const normalizedRouteBucketId = getAssetRouteBucketId(
    assetRouteBucketToFilterValue(routeBucketId),
  );
  if (
    validateCustomStepIds &&
    !isValidAssetRouteBucketId(normalizedRouteBucketId, sessionStepIds)
  ) {
    return ASSET_ROUTE_ALL;
  }
  return normalizedRouteBucketId;
}

export function persistAssetsRouteBucketId(sessionId: string, routeBucketId: string): void {
  try {
    localStorage.setItem(LAST_ASSETS_BUCKET_STORAGE_KEY(sessionId), routeBucketId);
  } catch {
    // localStorage might be unavailable in some contexts
  }
}

export function resolveEffectiveAssetSourceFilter({
  activeSourceFilter,
  defaultSourceIds = [],
  defaultsApplied,
}: ResolveEffectiveAssetSourceFilterArgs): string[] {
  if (activeSourceFilter.length > 0) {
    return activeSourceFilter;
  }

  if (!defaultsApplied && defaultSourceIds.length > 0) {
    return [...defaultSourceIds];
  }

  return [];
}

export function resolveAssetStatusFilter(
  statusFilterOverride: string | null | undefined,
  activeStatusFilter: string | null,
): string | null {
  return statusFilterOverride === undefined ? activeStatusFilter : statusFilterOverride;
}

export function buildConnectedCardFocusNavigation({
  cardId,
  sessionId,
  stepId,
  doneAt,
}: {
  cardId: string;
  sessionId: string;
  stepId?: string | null;
  doneAt?: string | Date | null;
}): ConnectedCardFocusNavigation {
  return {
    routeName: RouteNames.CARD,
    params: {
      sessionId,
      cardId,
    },
    stepId: doneAt ? STEP_TAB_DONE : stepId || STEP_TAB_TRIAGE,
  };
}

export function buildAssetBuckets({
  totalCount,
  doneCount,
  visibleSteps,
  pendingCount,
  noCardsConnectedCount,
  activeFilter = null,
}: BuildAssetBucketsArgs): AssetBucketDefinition[] {
  const buckets: AssetBucketDefinition[] = [
    {
      key: 'all',
      label: 'All',
      count: totalCount,
      filterValue: null,
      kind: 'all',
    },
  ];

  if (doneCount > 0 || activeFilter === 'final') {
    buckets.push({
      key: 'final',
      label: 'Done',
      count: doneCount,
      filterValue: 'final',
      kind: 'final',
    });
  }

  visibleSteps
    .slice()
    .reverse()
    .forEach((step) => {
      if (step.count > 0 || activeFilter === step.stepId) {
        buckets.push({
          key: step.stepId,
          label: step.label,
          count: step.count,
          filterValue: step.stepId,
          kind: 'step',
        });
      }
    });

  if (pendingCount > 0 || activeFilter === STEP_TAB_TRIAGE) {
    buckets.push({
      key: STEP_TAB_TRIAGE,
      label: PENDING_ASSET_LABEL,
      count: pendingCount,
      filterValue: STEP_TAB_TRIAGE,
      kind: 'pending',
    });
  }

  if (noCardsConnectedCount > 0 || activeFilter === 'not_started') {
    buckets.push({
      key: 'not_started',
      label: NO_CARDS_CONNECTED_LABEL,
      count: noCardsConnectedCount,
      filterValue: 'not_started',
      kind: 'not_started',
    });
  }

  return buckets;
}

export function buildSearchAssetBucketItems({
  assets,
  totalAssets = assets,
  statusMap,
  steps,
  previewLimit = 10,
}: {
  assets: AssetItem[];
  totalAssets?: AssetItem[];
  statusMap: Map<string, AssetWorkflowStatus>;
  steps: Array<{ stepId: string; label: string }>;
  previewLimit?: number;
}): SearchAssetBucketItem[] {
  const countsByBucket: Record<string, number> = {};
  const totalCountsByBucket: Record<string, number> = {};

  assets.forEach((asset) => {
    const status = statusMap.get(asset.rowId);
    if (!status) return;
    const key = status.kind === 'step' ? status.stepId : status.kind;
    countsByBucket[key] = (countsByBucket[key] || 0) + 1;
  });

  totalAssets.forEach((asset) => {
    const status = statusMap.get(asset.rowId);
    if (!status) return;
    const key = status.kind === 'step' ? status.stepId : status.kind;
    totalCountsByBucket[key] = (totalCountsByBucket[key] || 0) + 1;
  });

  const filterAssetsForBucket = (filterValue: string | null) =>
    assets.filter((asset) => {
      if (filterValue === null) return true;
      const status = statusMap.get(asset.rowId);
      if (!status) return false;
      return (
        filterValue === status.kind || (status.kind === 'step' && filterValue === status.stepId)
      );
    });

  return buildAssetBuckets({
    totalCount: assets.length,
    doneCount: countsByBucket.final || 0,
    visibleSteps: steps.map((step) => ({
      stepId: step.stepId,
      label: step.label,
      count: countsByBucket[step.stepId] || 0,
    })),
    pendingCount: countsByBucket[STEP_TAB_TRIAGE] || 0,
    noCardsConnectedCount: countsByBucket.not_started || 0,
  }).map((bucket) => ({
    ...bucket,
    totalCount:
      bucket.filterValue === null
        ? totalAssets.length
        : totalCountsByBucket[bucket.filterValue] || 0,
    previewAssets: filterAssetsForBucket(bucket.filterValue).slice(0, previewLimit),
  }));
}

export function matchesAssetSearch({
  query,
  rowId,
  assetLabel,
  fieldValues,
  connectedCardTitles,
}: {
  query: string;
  rowId: string;
  assetLabel: string;
  fieldValues: string[];
  connectedCardTitles: string[];
}): boolean {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) {
    return false;
  }

  const matchesValue = (value: string) => normalizeSearchText(value).includes(normalizedQuery);

  if (matchesValue(rowId) || matchesValue(assetLabel)) {
    return true;
  }

  if (fieldValues.some((value) => matchesValue(value))) {
    return true;
  }

  return connectedCardTitles.some((title) => matchesValue(title));
}

export type DetailPanelView = 'single' | 'multi' | 'confirm';
export type CollectionsSidebarMode = 'tree' | 'detail' | 'multi';
export type AssetsMetaFilter = 'all' | 'missing';
export type AssetsGroupBy = 'step' | 'collection';

export interface CollectionMemberFilter {
  status?: CollectionMemberStatus;
  scheduledDateIso?: string;
  /** Parent-collection members not assigned to any child subfolder. */
  unsorted?: boolean;
}

export const ASSETS_GROUP_BY_LABELS: Record<AssetsGroupBy, string> = {
  step: 'Step',
  collection: 'Collection',
};

export { toCalendarIsoDate } from './collection-status-sections';

export function formatAssetsDateFilterLabel(
  start: string | null,
  end: string | null,
): string | null {
  if (!start) return null;
  const formatShort = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };
  const endIso = end ?? start;
  if (endIso === start) {
    return formatShort(start);
  }
  return `${formatShort(start)} – ${formatShort(endIso)}`;
}

export function parseCalendarIsoDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  // UTC noon keeps the calendar day stable across timezones and aligns with cron UTC cutoff
  return new Date(Date.UTC(y, m - 1, d, 12, 0, 0, 0));
}

// ─── Shared status colors ────────────────────────────────────────────────────

export function getAssetStatusColors(kind: string): { bg: string; text: string; border: string } {
  switch (kind) {
    case 'final':
      return {
        bg: 'rgba(34, 197, 94, 0.15)',
        text: '#22c55e',
        border: 'rgba(34, 197, 94, 0.3)',
      };
    case 'step':
      return {
        bg: 'rgba(234, 179, 8, 0.2)',
        text: '#eab308',
        border: 'rgba(234, 179, 8, 0.3)',
      };
    case 'not_started':
    default:
      return {
        bg: 'rgba(255, 255, 255, 0.08)',
        text: 'rgba(255, 255, 255, 0.5)',
        border: 'rgba(255, 255, 255, 0.12)',
      };
  }
}

// ─── Store ──────────────────────────────────────────────────────────────────

export class UiAssetsStore {
  private rootStore: RootStore;
  private disposers: (() => void)[] = [];
  private defaultsApplied = false; // tracks whether we've applied defaults for the current session

  // Observable state
  searchQuery = '';
  activeStatusFilter: string | null = null; // null = All
  activeSourceFilter: string[] = [];
  selectedAssetIds: ObservableSet<string> = observable.set();
  selectedAssetOrder: string[] = [];
  detailPanelAssetId: string | null = null;
  detailPanelSourceId: string | null = null;
  detailPanelView: DetailPanelView = 'single';
  sidebarOpen = false;
  sidebarMode: CollectionsSidebarMode = 'tree';
  activeCollectionFilter: string | null = null;
  activeCollectionMemberFilter: CollectionMemberFilter | null = null;
  assetsMetaFilter: AssetsMetaFilter = 'all';
  groupBy: AssetsGroupBy = 'step';
  dateFilterStart: string | null = null;
  dateFilterEnd: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeObservable(this, {
      searchQuery: observable,
      activeStatusFilter: observable,
      activeSourceFilter: observable,
      selectedAssetIds: observable,
      selectedAssetOrder: observable,
      detailPanelAssetId: observable,
      detailPanelSourceId: observable,
      detailPanelView: observable,
      sidebarOpen: observable,
      sidebarMode: observable,
      activeCollectionFilter: observable,
      activeCollectionMemberFilter: observable,
      assetsMetaFilter: observable,
      groupBy: observable,
      dateFilterStart: observable,
      dateFilterEnd: observable,

      reverseCardIndex: computed,
      allAssets: computed,
      statusMap: computed,
      statusCounts: computed,
      sourceCounts: computed,
      isCollectionsSidebarVisible: computed,
      isCollectionsTreeVisible: computed,
      effectiveSourceFilterIds: computed,
      hasActiveDateFilter: computed,
      isSelectionMode: computed,

      setSearchQuery: action.bound,
      setStatusFilter: action.bound,
      setSourceFilter: action.bound,
      clearSourceFilters: action.bound,
      prepareForJumpNavigation: action.bound,
      toggleCheckAsset: action.bound,
      clearSelection: action.bound,
      openDetailPanel: action.bound,
      closeDetailPanel: action.bound,
      setDetailPanelView: action.bound,
      setSidebarOpen: action.bound,
      toggleCollectionsFromToolbar: action.bound,
      setSidebarMode: action.bound,
      setCollectionFilter: action.bound,
      clearCollectionFilter: action.bound,
      handleAssetRemovedFromCollection: action.bound,
      reconcileActiveCollectionMemberFilter: action.bound,
      setAssetsMetaFilter: action.bound,
      setGroupBy: action.bound,
      setDateFilter: action.bound,
      clearDateFilter: action.bound,
      backToCollectionsTree: action.bound,
      closeCollectionsSidebar: action.bound,
      handleCollectionsEscape: action.bound,
    });

    // Clear selection/panel when session changes
    this.disposers.push(
      reaction(
        () => rootStore.sessionsStore.currentSessionId,
        () => {
          runInAction(() => {
            this.clearSelection();
            this.closeCollectionsSidebar();
            this.searchQuery = '';
            this.activeStatusFilter = null;
            this.activeSourceFilter = [];
            this.sidebarOpen = false;
            this.sidebarMode = 'tree';
            this.activeCollectionFilter = null;
            this.activeCollectionMemberFilter = null;
            this.assetsMetaFilter = 'all';
            this.groupBy = 'step';
            this.dateFilterStart = null;
            this.dateFilterEnd = null;
            this.defaultsApplied = false; // allow defaults to be applied once new session data arrives
          });
        },
      ),
    );

    // Apply saved defaults once per session load — fires when Firestore delivers assetTabPreferences
    // (which arrives async after currentSessionId changes, so the above reaction can't read it yet)
    this.disposers.push(
      reaction(
        () => rootStore.sessionsStore.currentSession?.data.assetTabPreferences?.defaultSourceIds,
        (defaults) => {
          if (!this.defaultsApplied && defaults?.length) {
            runInAction(() => {
              this.activeSourceFilter = [...defaults];
              this.defaultsApplied = true;
            });
          }
        },
      ),
    );
  }

  dispose() {
    this.disposers.forEach((d) => d());
    this.disposers = [];
  }

  // ─── Computed ────────────────────────────────────────────────────────────

  /**
   * Reverse card index: rowId → Set<cardId>
   * One pass over all cards + their connections.
   */
  get reverseCardIndex(): Map<string, Set<string>> {
    const { cardsStore, sessionsStore } = this.rootStore;
    const session = sessionsStore.currentSession;
    if (!session) return new Map();

    const index = new Map<string, Set<string>>();
    const modules = session.modules;

    Object.values(cardsStore.cards).forEach((card) => {
      if (card.data.deletedAt) return;

      modules.forEach((mod) => {
        const rowIds = card.getConnectedRows(mod.id);
        rowIds.forEach((rowId) => {
          let set = index.get(rowId);
          if (!set) {
            set = new Set();
            index.set(rowId, set);
          }
          set.add(card.id);
        });
      });
    });

    return index;
  }

  /**
   * Two-tier aggregation: gather assets per-source, then flatten.
   * MobX only invalidates per-source when that source's rows change.
   */
  get allAssets(): AssetItem[] {
    const { sourcesStore } = this.rootStore;
    const assets: AssetItem[] = [];

    Object.values(sourcesStore.sources).forEach((source) => {
      const rows = source.orderedRows;
      rows.forEach((row) => {
        if (row.getUnifiedImageUrl(400)) {
          assets.push({ rowId: row.id, sourceId: source.id });
        }
      });
    });

    return assets;
  }

  /**
   * Pre-computed status for every asset row. Single pass with O(1) step lookups.
   * All consumers read from this map instead of calling getAssetStatus() per-row.
   */
  get statusMap(): Map<string, AssetWorkflowStatus> {
    const { cardsStore, sessionsStore } = this.rootStore;
    const session = sessionsStore.currentSession;
    if (!session) return new Map();

    const steps = session.sortedSteps;
    // Pre-build stepId → index lookup (O(S) once, not O(S) per card)
    const stepIndexMap = new Map<string, number>();
    steps.forEach((s, i) => stepIndexMap.set(s.id, i));

    const reverseIndex = this.reverseCardIndex;
    const result = new Map<string, AssetWorkflowStatus>();

    this.allAssets.forEach((asset) => {
      const cardIds = reverseIndex.get(asset.rowId);
      if (!cardIds || cardIds.size === 0) {
        result.set(asset.rowId, { kind: 'not_started', label: NO_CARDS_CONNECTED_LABEL });
        return;
      }

      let bestStepIndex = -1;
      let bestStepName = '';
      let bestStepId = '';
      let hasDone = false;
      let hasAnyCard = false; // true if at least one non-deleted card found

      cardIds.forEach((cardId) => {
        const card = cardsStore.cards[cardId];
        if (!card) return;
        hasAnyCard = true;

        if (card.data.doneAt) {
          hasDone = true;
          return;
        }

        const stepId = card.data.stepId;
        if (stepId) {
          const stepIndex = stepIndexMap.get(stepId);
          if (stepIndex !== undefined && stepIndex > bestStepIndex) {
            bestStepIndex = stepIndex;
            bestStepName = steps[stepIndex].name;
            bestStepId = steps[stepIndex].id;
          }
        }
      });

      if (hasDone) {
        result.set(asset.rowId, { kind: 'final', label: 'Done' });
      } else if (bestStepId) {
        result.set(asset.rowId, { kind: 'step', label: bestStepName, stepId: bestStepId });
      } else if (hasAnyCard) {
        // Connected to card(s) but no named step assigned yet (card is in triage/pending)
        result.set(asset.rowId, {
          kind: 'step',
          label: PENDING_ASSET_LABEL,
          stepId: STEP_TAB_TRIAGE,
        });
      } else {
        result.set(asset.rowId, { kind: 'not_started', label: NO_CARDS_CONNECTED_LABEL });
      }
    });

    return result;
  }

  /**
   * Filter assets by source, status, and search query.
   * Search query is passed in from component (debounced).
   */
  getFilteredAssets(
    debouncedQuery: string,
    options?: { statusFilter?: string | null; sourceFilterIds?: string[] },
  ): AssetItem[] {
    let assets = this.allAssets;
    const sourceFilterIds = options?.sourceFilterIds ?? this.activeSourceFilter;
    const statusFilter = resolveAssetStatusFilter(options?.statusFilter, this.activeStatusFilter);

    // 1. Source filter first (cheapest — string comparison)
    if (sourceFilterIds.length > 0) {
      assets = assets.filter((a) => sourceFilterIds.includes(a.sourceId));
    }

    // 2. Collection / meta filter
    if (this.activeCollectionFilter) {
      const collectionId = this.activeCollectionFilter;
      const members =
        this.rootStore.assetCollectionsStore.membersByCollectionId[collectionId] ?? {};
      assets = assets.filter((asset) => {
        const member = members[getAssetKey(asset.sourceId, asset.rowId)];
        if (!member) {
          return false;
        }
        if (!this.activeCollectionMemberFilter) {
          return true;
        }
        if (this.activeCollectionMemberFilter.unsorted) {
          const unsortedKeys =
            this.rootStore.assetCollectionsStore.getUnsortedMemberKeys(collectionId);
          return unsortedKeys.has(getAssetKey(asset.sourceId, asset.rowId));
        }
        if (!this.activeCollectionMemberFilter.status) {
          return true;
        }
        return matchesCollectionMemberFilter(
          member,
          this.activeCollectionMemberFilter.status,
          this.activeCollectionMemberFilter.scheduledDateIso,
        );
      });
    } else if (this.assetsMetaFilter === 'missing') {
      const memberKeys = this.rootStore.assetCollectionsStore.allMemberAssetKeys;
      assets = assets.filter((asset) => !memberKeys.has(getAssetKey(asset.sourceId, asset.rowId)));
    }

    // 2b. Scheduled date filter (any collection membership with scheduledDate in range)
    if (this.dateFilterStart) {
      const endIso = this.dateFilterEnd ?? this.dateFilterStart;
      const collStore = this.rootStore.assetCollectionsStore;
      assets = assets.filter((asset) => {
        const scheduledDates = collStore.getScheduledDatesForAsset(asset.sourceId, asset.rowId);
        return scheduledDates.some((iso) => iso >= this.dateFilterStart! && iso <= endIso);
      });
    }

    // 3. Status filter (O(1) from statusMap)
    if (statusFilter) {
      const statusMap = this.statusMap;
      assets = assets.filter((asset) => {
        const status = statusMap.get(asset.rowId);
        if (!status) return false;
        return (
          statusFilter === status.kind || (status.kind === 'step' && statusFilter === status.stepId)
        );
      });
    }

    // 4. Search last (most expensive)
    const query = debouncedQuery.trim();
    if (query) {
      const { sourcesStore, cardsStore } = this.rootStore;

      assets = assets.filter((asset) => {
        const source = sourcesStore.sources[asset.sourceId];
        if (!source) return false;

        const row = source.rows[asset.rowId];
        if (!row) return false;

        // Search asset ID / display label (e.g. "Asset 01GRSH" when no filename)
        const assetLabel = row.image?.originalFilename || `Asset ${asset.rowId.slice(0, 6)}`;
        const fieldIds = source.data.fieldIds || [];
        const cardIds = this.reverseCardIndex.get(asset.rowId);

        return matchesAssetSearch({
          query,
          rowId: asset.rowId,
          assetLabel,
          fieldValues: fieldIds
            .map((fieldId) => row.getValue(fieldId))
            .filter((value): value is string => !!value)
            .map(String),
          connectedCardTitles: Array.from(cardIds || [])
            .map((cardId) => {
              const card = cardsStore.cards[cardId];
              return card ? card.data.manualTitle || card.filenameInfo?.filename || '' : '';
            })
            .filter(Boolean),
        });
      });
    }

    return assets;
  }

  /**
   * Count assets by status kind for filter chip badges.
   * Scoped to active source filter so counts match the visible grid.
   */
  get statusCounts(): Record<string, number> {
    const counts: Record<string, number> = {};
    const statusMap = this.statusMap;

    let assets = this.allAssets;
    if (this.activeSourceFilter.length > 0) {
      assets = assets.filter((a) => this.activeSourceFilter.includes(a.sourceId));
    }

    assets.forEach((asset) => {
      const status = statusMap.get(asset.rowId);
      if (!status) return;

      const key = status.kind === 'step' ? status.stepId : status.kind;
      counts[key] = (counts[key] || 0) + 1;
    });

    return counts;
  }

  /**
   * Count assets per source for source filter chip badges.
   */
  get sourceCounts(): Record<string, number> {
    const counts: Record<string, number> = {};
    this.allAssets.forEach((a) => {
      counts[a.sourceId] = (counts[a.sourceId] || 0) + 1;
    });
    return counts;
  }

  get isCollectionsSidebarVisible(): boolean {
    return this.sidebarOpen;
  }

  /** Collections tree is shown (sidebar open and not in asset detail/multi). */
  get isCollectionsTreeVisible(): boolean {
    return this.sidebarOpen && this.sidebarMode === 'tree';
  }

  get hasActiveDateFilter(): boolean {
    return this.dateFilterStart !== null;
  }

  /** After the first checkbox select, card clicks toggle selection instead of opening detail. */
  get isSelectionMode(): boolean {
    return this.selectedAssetIds.size > 0;
  }

  get effectiveSourceFilterIds(): string[] {
    const defaultSourceIds =
      this.rootStore.sessionsStore.currentSession?.data.assetTabPreferences?.defaultSourceIds ?? [];

    return resolveEffectiveAssetSourceFilter({
      activeSourceFilter: this.activeSourceFilter,
      defaultSourceIds,
      defaultsApplied: this.defaultsApplied,
    });
  }

  // ─── Actions ─────────────────────────────────────────────────────────────

  setSearchQuery(query: string) {
    this.searchQuery = query;
  }

  setStatusFilter(filter: string | null) {
    this.activeStatusFilter = filter;
  }

  setSourceFilter(sourceId: string | null): void {
    if (sourceId === null) {
      this.activeSourceFilter = [];
      return;
    }
    const idx = this.activeSourceFilter.indexOf(sourceId);
    if (idx >= 0) {
      this.activeSourceFilter.splice(idx, 1);
    } else {
      this.activeSourceFilter.push(sourceId);
    }
  }

  clearSourceFilters(): void {
    this.activeSourceFilter = [];
    this.defaultsApplied = true;
  }

  prepareForJumpNavigation(filterValue: string | null): void {
    this.searchQuery = '';
    this.clearSourceFilters();
    this.setStatusFilter(filterValue);
  }

  toggleCheckAsset(assetKey: string) {
    if (this.selectedAssetIds.has(assetKey)) {
      this.selectedAssetIds.delete(assetKey);
      this.selectedAssetOrder = this.selectedAssetOrder.filter((k) => k !== assetKey);
    } else {
      this.selectedAssetIds.add(assetKey);
      this.selectedAssetOrder.push(assetKey);
    }

    // Reset sidebar when selection is fully cleared — otherwise follow PRD state machine.
    if (this.selectedAssetIds.size === 0) {
      this.syncSidebarFromSelection();
      return;
    }

    if (!this.sidebarOpen) {
      this.sidebarOpen = true;
    }
    this.syncSidebarFromSelection();
  }

  private syncSidebarFromSelection() {
    const count = this.selectedAssetIds.size;
    if (count === 0) {
      this.detailPanelAssetId = null;
      this.detailPanelSourceId = null;
      this.detailPanelView = 'single';
      this.sidebarMode = 'tree';
      return;
    }

    if (count === 1) {
      const [onlyKey] = this.selectedAssetOrder;
      this.detailPanelAssetId = onlyKey;
      this.detailPanelSourceId =
        this.allAssets.find((a) => getAssetKey(a.sourceId, a.rowId) === onlyKey)?.sourceId ?? null;
      this.detailPanelView = 'single';
      this.sidebarMode = 'detail';
      return;
    }

    this.detailPanelView = 'multi';
    this.sidebarMode = 'multi';
  }

  clearSelection() {
    this.selectedAssetIds.clear();
    this.selectedAssetOrder = [];
    this.syncSidebarFromSelection();
  }

  openDetailPanel(assetKey: string, sourceId?: string) {
    this.detailPanelAssetId = assetKey;
    this.detailPanelSourceId =
      sourceId ??
      this.allAssets.find((a) => getAssetKey(a.sourceId, a.rowId) === assetKey)?.sourceId ??
      null;
    this.detailPanelView = 'single';
    this.sidebarOpen = true;
    this.sidebarMode = 'detail';
  }

  backToCollectionsTree() {
    this.detailPanelAssetId = null;
    this.detailPanelSourceId = null;
    this.detailPanelView = 'single';
    this.sidebarMode = 'tree';
    this.clearSelection();
  }

  closeCollectionsSidebar() {
    this.sidebarOpen = false;
    this.detailPanelAssetId = null;
    this.detailPanelSourceId = null;
    this.detailPanelView = 'single';
    this.sidebarMode = 'tree';
  }

  closeDetailPanel() {
    if (this.sidebarOpen && this.sidebarMode !== 'tree') {
      this.backToCollectionsTree();
      return;
    }
    this.closeCollectionsSidebar();
  }

  handleCollectionsEscape() {
    if (this.sidebarMode !== 'tree') {
      this.backToCollectionsTree();
      return;
    }
    if (this.sidebarOpen) {
      this.closeCollectionsSidebar();
    }
  }

  setSidebarOpen(open: boolean) {
    if (open) {
      this.sidebarOpen = true;
      return;
    }
    this.closeCollectionsSidebar();
  }

  /** Toolbar Collections chip: restore tree from detail, or open/close sidebar. */
  toggleCollectionsFromToolbar() {
    if (this.sidebarOpen && this.sidebarMode !== 'tree') {
      this.backToCollectionsTree();
      return;
    }
    this.setSidebarOpen(!this.sidebarOpen);
  }

  setSidebarMode(mode: CollectionsSidebarMode) {
    this.sidebarMode = mode;
  }

  setCollectionFilter(collectionId: string | null, memberFilter?: CollectionMemberFilter | null) {
    this.activeCollectionFilter = collectionId;
    this.activeCollectionMemberFilter = memberFilter ?? null;
    if (collectionId) {
      this.assetsMetaFilter = 'all';
    }
  }

  /** Clear sidebar selection/detail and collection subfolder filter after a member is removed. */
  handleAssetRemovedFromCollection(sourceId: string, rowId: string, collectionId: string) {
    const assetKey = getAssetKey(sourceId, rowId);
    let selectionChanged = false;

    if (this.selectedAssetIds.has(assetKey)) {
      this.selectedAssetIds.delete(assetKey);
      this.selectedAssetOrder = this.selectedAssetOrder.filter((key) => key !== assetKey);
      selectionChanged = true;
    }

    if (this.detailPanelAssetId === assetKey) {
      this.detailPanelAssetId = null;
      this.detailPanelSourceId = null;
      if (this.selectedAssetIds.size === 0) {
        this.sidebarMode = 'tree';
        this.detailPanelView = 'single';
      }
    }

    if (selectionChanged) {
      this.syncSidebarFromSelection();
    }

    if (this.activeCollectionFilter === collectionId) {
      this.reconcileActiveCollectionMemberFilter(collectionId);
    }
  }

  /**
   * Drop the active Pending/Used/Scheduled subfolder filter when no members match it anymore
   * (e.g. after status change or remove).
   */
  reconcileActiveCollectionMemberFilter(collectionId: string) {
    if (this.activeCollectionFilter !== collectionId || !this.activeCollectionMemberFilter) {
      return;
    }

    const { assetCollectionsStore } = this.rootStore;
    const filter = this.activeCollectionMemberFilter;

    if (filter.unsorted) {
      if (assetCollectionsStore.getUnsortedMemberKeys(collectionId).size === 0) {
        this.activeCollectionMemberFilter = null;
      }
      return;
    }

    if (!filter.status) {
      return;
    }

    const members = assetCollectionsStore.membersByCollectionId[collectionId] ?? {};
    const hasMatch = Object.values(members).some((member) =>
      matchesCollectionMemberFilter(member, filter.status!, filter.scheduledDateIso),
    );

    if (!hasMatch) {
      this.activeCollectionMemberFilter = null;
    }
  }

  clearCollectionFilter() {
    this.activeCollectionFilter = null;
    this.activeCollectionMemberFilter = null;
    this.assetsMetaFilter = 'all';
  }

  setAssetsMetaFilter(filter: AssetsMetaFilter) {
    this.assetsMetaFilter = filter;
    if (filter === 'missing') {
      this.activeCollectionFilter = null;
      this.activeCollectionMemberFilter = null;
    }
  }

  setGroupBy(groupBy: AssetsGroupBy) {
    this.groupBy = groupBy;
  }

  setDateFilter(start: string | null, end?: string | null) {
    this.dateFilterStart = start;
    this.dateFilterEnd = start === null ? null : end ?? start;
  }

  clearDateFilter() {
    this.dateFilterStart = null;
    this.dateFilterEnd = null;
  }

  setDetailPanelView(view: DetailPanelView) {
    this.detailPanelView = view;
  }
}
